import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import Header from "../components/Common/Header";
import {
  MemorySummary,
  MemoryCreate,
  listMemories,
  createMemory,
  deleteMemory,
} from "../api/memories";
import { LocationPicker } from "../components/Location";
import { PersonPicker } from "../components/Person";

export default function Memories() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<MemorySummary | null>(null);

  // Form state for creating
  const [newMemory, setNewMemory] = useState<MemoryCreate>({
    title: "",
    description: "",
    memory_date: "",
    location_id: undefined,
    person_ids: [],
    connected_person_ids: [],
  });

  // Fetch memories
  const { data: memories, isLoading, error } = useQuery({
    queryKey: ["memories", search],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return listMemories(token, { search: search || undefined, limit: 100 });
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: MemoryCreate) => {
      const token = await getAccessTokenSilently();
      return createMemory(token, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["memories"] });
      setShowCreateModal(false);
      setNewMemory({
        title: "",
        description: "",
        memory_date: "",
        location_id: undefined,
        person_ids: [],
        connected_person_ids: [],
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const token = await getAccessTokenSilently();
      return deleteMemory(token, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["memories"] });
      setDeleteConfirm(null);
    },
  });

  const handleCreate = () => {
    if (!newMemory.title) return;
    createMutation.mutate(newMemory);
  };

  return (
    <div className="min-h-screen bg-vault-50">
      <Header />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-4">
          <Link to="/dashboard" className="text-sm text-vault-600 hover:text-vault-900">
            ← Back to Dashboard
          </Link>
        </nav>

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-bold text-vault-900">
              Memories
            </h1>
            <p className="mt-1 text-vault-600">
              Events and moments that bring together people and photos
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="rounded-lg bg-vault-600 px-4 py-2 text-white hover:bg-vault-700"
          >
            + Add Memory
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search memories..."
            className="w-full max-w-md rounded-lg border border-vault-200 px-4 py-2"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-vault-300 border-t-vault-600" />
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="rounded-lg bg-red-50 p-4 text-red-700">
            Failed to load memories. Please try again.
          </div>
        )}

        {/* Empty state */}
        {!isLoading && memories && memories.length === 0 && (
          <div className="rounded-lg bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-purple-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-vault-900">No memories yet</h3>
            <p className="mt-1 text-vault-600">
              Create memories like &ldquo;Christmas 1965&rdquo; or &ldquo;Wedding of John &amp; Mary&rdquo;
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 rounded-lg bg-vault-600 px-4 py-2 text-white hover:bg-vault-700"
            >
              Create Your First Memory
            </button>
          </div>
        )}

        {/* List */}
        {memories && memories.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {memories.map((memory) => (
              <div
                key={memory.id}
                className="rounded-lg bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <Link to={`/memories/${memory.id}`} className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-vault-900 truncate">
                          {memory.title}
                        </h3>
                        <div className="flex flex-wrap gap-x-2 text-sm text-vault-500">
                          {memory.memory_date && (
                            <span>{memory.memory_date}</span>
                          )}
                          {memory.location_name && (
                            <span>· {memory.location_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {(memory.media_count > 0 || memory.person_count > 0) && (
                      <div className="mt-2 flex gap-3 text-xs text-vault-500">
                        {memory.media_count > 0 && (
                          <span>{memory.media_count} photos</span>
                        )}
                        {memory.person_count > 0 && (
                          <span>{memory.person_count} people</span>
                        )}
                      </div>
                    )}
                  </Link>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setDeleteConfirm(memory);
                    }}
                    className="p-1 text-vault-400 hover:text-red-600"
                    title="Delete"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h2 className="mb-4 text-xl font-semibold text-vault-900">
              Add Memory
            </h2>
            <p className="mb-4 text-sm text-vault-600">
              Create a memory to group photos and people around an event or occasion.
            </p>

            {createMutation.error && (
              <div className="mb-4 rounded bg-red-50 p-3 text-sm text-red-700">
                {createMutation.error instanceof Error ? createMutation.error.message : "Failed to create"}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-vault-700">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder='e.g., "Christmas 1965" or "Wedding of John & Mary"'
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  value={newMemory.title}
                  onChange={(e) => setNewMemory({ ...newMemory, title: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">
                  Description
                </label>
                <textarea
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  rows={3}
                  placeholder="What makes this memory special?"
                  value={newMemory.description}
                  onChange={(e) => setNewMemory({ ...newMemory, description: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">
                  Date
                </label>
                <input
                  type="text"
                  placeholder='e.g., "25 December 1965" or "Summer 1970"'
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  value={newMemory.memory_date}
                  onChange={(e) => setNewMemory({ ...newMemory, memory_date: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">
                  Location
                </label>
                <LocationPicker
                  value={newMemory.location_id || null}
                  onChange={(id) => setNewMemory({ ...newMemory, location_id: id || undefined })}
                  placeholder="Where did this happen?"
                  className="mt-1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">
                  People
                </label>
                <PersonPicker
                  selectedPersonIds={newMemory.person_ids || []}
                  selectedConnectedPersonIds={newMemory.connected_person_ids || []}
                  onAddPerson={(id) => setNewMemory({ 
                    ...newMemory, 
                    person_ids: [...(newMemory.person_ids || []), id] 
                  })}
                  onAddConnectedPerson={(id) => setNewMemory({ 
                    ...newMemory, 
                    connected_person_ids: [...(newMemory.connected_person_ids || []), id] 
                  })}
                  onRemovePerson={(id) => setNewMemory({ 
                    ...newMemory, 
                    person_ids: (newMemory.person_ids || []).filter((p) => p !== id) 
                  })}
                  onRemoveConnectedPerson={(id) => setNewMemory({ 
                    ...newMemory, 
                    connected_person_ids: (newMemory.connected_person_ids || []).filter((p) => p !== id) 
                  })}
                  placeholder="Who was there?"
                  className="mt-1"
                />
                <p className="mt-1 text-xs text-vault-500">
                  You can add more people later from the memory detail page
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending || !newMemory.title}
                className="rounded-lg bg-vault-600 px-4 py-2 text-white hover:bg-vault-700 disabled:opacity-50"
              >
                {createMutation.isPending ? "Creating..." : "Create Memory"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-vault-900">
              Delete Memory?
            </h2>
            <p className="mb-4 text-vault-600">
              Are you sure you want to delete{" "}
              <span className="font-medium">{deleteConfirm.title}</span>?
              This will remove all tags from this memory but won&apos;t delete the photos or people.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                disabled={deleteMutation.isPending}
                className="rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
